'use client';

import { useEffect, useState } from 'react';
import Image from 'next/image';
import Navbar from '@/components/navbar';
import FlightSearchForm from '@/components/flight-search-form';
import FlightSearchFormMobile from '@/components/flight-search-form-mobile';
import StickySearchInput from '@/components/sticky-search-input';
import SearchModal from '@/components/search-modal';
import { getNearbyAirports, type Airport } from '@/lib/airports';
import { type Coordinates } from '@/app/page';
import { useFlightSearch } from '@/hooks/useFlightSearch';
import { motion, AnimatePresence } from 'framer-motion';
import { useRef } from 'react';
import { loadAirlinesContent, saveAirlinesContent, type AirlineContent } from '@/lib/airlinesAdmin';
import AirlineDealsSection from '@/components/airline-deals-section';
import AirlineInfoSection from '@/components/airline-info-section';

interface AirlinePageProps {
  slug: string;
}

// Default fallback content
const DEFAULT_FALLBACK_CONTENT: AirlineContent = {
  slug: 'emirates-airlines',
  title: 'Emirates Airlines',
  subtitle: 'The Art of Flying',
  description: 'Emirates offers first and business class flights with private space, attentive service, and modern cabins that make international journeys effortless.',
  heroImage: '/images/airlines/Emirates-Airlines-min.jpg',
  introTitle: 'The Emirates Experience',
  introText: 'Emirates has set benchmarks in luxury travel. Every flight blends comfort with scale, turning the idea of long-haul flying into a refined experience.',
  ctaTitle: 'Book Your Emirates Flight Today',
  ctaText: 'Join millions of satisfied passengers who choose Emirates for their business and first-class travel. Experience the difference that true luxury makes.',
  feature: 'Experience the world\'s most luxurious airline with Emirates\' award-winning service. Enjoy state-of-the-art entertainment systems, complimentary Wi-Fi, and access to exclusive lounges. Our commitment to excellence ensures every journey is memorable from takeoff to landing.',
  seat: 'Spacious lie-flat seats in First and Business Class with direct aisle access. Each seat converts into a comfortable bed with premium bedding. Personal storage space and adjustable privacy screens create your personal sanctuary in the sky.',
  dining: 'Gourmet cuisine crafted by world-renowned chefs featuring international flavors. Premium wine selection and fine dining service. Meals are served on fine china with elegant presentation and attention to detail.',
  features: [
    {
      title: 'Iconic A380 Fleet',
      description: 'Experience the world\'s largest passenger aircraft with onboard lounges, showers, and spacious cabins.'
    },
    {
      title: 'Dubai Hub Connectivity',
      description: 'Connect to over 150 destinations worldwide through our state-of-the-art Dubai International hub.'
    },
    {
      title: 'Award-Winning Service',
      description: 'Our cabin crew delivers personalized service that has earned Emirates numerous international awards.'
    }
  ],
  routes: [
    { from: 'London', to: 'Dubai', duration: '7h 10m', aircraft: 'A380' },
    { from: 'New York', to: 'Dubai', duration: '12h 30m', aircraft: 'B777' },
    { from: 'Dubai', to: 'Tokyo', duration: '8h 35m', aircraft: 'A380' },
    { from: 'Dubai', to: 'Sydney', duration: '14h 25m', aircraft: 'A380' }
  ],
  classes: [
    {
      name: 'First Class',
      description: 'Private suites with shower spa, fine dining, and chauffeur service',
      features: ['Private suite with sliding doors', 'Onboard shower spa', 'Chauffeur drive service', 'Gourmet dining']
    },
    {
      name: 'Business Class',
      description: 'Lie-flat seats, premium dining, and priority boarding',
      features: ['Lie-flat seats', 'Premium dining', 'Priority boarding', 'Lounge access']
    }
  ]
};



const IMAGE_FALLBACKS: Record<string, string> = {
  'emirates-airlines': '/images/london-big.jpg',
  'singapore-airlines': '/images/paris.jpg',
  'lufthansa-airlines': '/images/rome.jpg',
  'american-airlines': '/images/madrid.jpg',
  'nippon-airlines': '/images/athens.jpg',
  'qantas-airlines': '/images/lisbon.jpg',
  'turkish-airlines': '/images/london.jpg',
  'etihad-airlines': '/images/dubai.jpg',
  'british-airlines': '/images/london.jpg',
  'united-airlines': '/images/chicago.jpg',
  'qatar-airlines': '/images/doha.jpg'
};

const getHeroImage = (slug: string, airlineContent: AirlineContent): string => {
  // Always use heroImage from airline content if available
  if (airlineContent.heroImage) {
    return airlineContent.heroImage;
  }
  // Fallback to airline-specific images from /images/airlines/
  const airlineImages: Record<string, string> = {
    'emirates-airlines': '/images/airlines/Emirates-Airlines-min.jpg',
    'singapore-airlines': '/images/airlines/Singapore-Airlines-min.jpg',
    'lufthansa-airlines': '/images/airlines/Lufthansa-Airlines-min.jpg',
    'american-airlines': '/images/airlines/American-Airlines-min.jpg',
    'nippon-airlines': '/images/airlines/Nippon-Airways-min.jpg',
    'qantas-airlines': '/images/airlines/Qantas-Airways-min.jpg',
    'turkish-airlines': '/images/airlines/Turkish-Airlines-min.jpg',
    'etihad-airlines': '/images/airlines/Etihad-Airlines-min.jpg',
    'british-airlines': '/images/airlines/British-Airways-min.jpg',
    'united-airlines': '/images/airlines/United-Airlines-min.jpg',
    'qatar-airlines': '/images/airlines/Qatar-Airways-min.jpg'
  };
  return airlineImages[slug] || '/images/london-big.jpg';
};

export default function AirlinePage({ slug }: AirlinePageProps) {
  const [airlineContent, setAirlineContent] = useState<AirlineContent | null>(null);
  const [coords, setCoords] = useState<Coordinates | null>(null);
  const [nearestAirport, setNearestAirport] = useState<Airport | null>(null);
  const [isSticky, setSticky] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const formRef = useRef<HTMLDivElement>(null);
  const searchState = useFlightSearch(nearestAirport);

  useEffect(() => {
    const loadContent = async () => {
      try {
        const allContent = await loadAirlinesContent();
        console.log('Loaded all airline content:', allContent);
        
        const content = allContent[slug];
        console.log(`Content for ${slug}:`, content);
        
        if (content) {
          setAirlineContent(content);
          console.log('Set airline content:', content);
          console.log('Feature/Seat/Dining data:', {
            feature: content.feature,
            seat: content.seat,
            dining: content.dining
          });
        } else {
          console.warn(`No content found for airline: ${slug}, using fallback`);
          setAirlineContent(DEFAULT_FALLBACK_CONTENT);
        }
      } catch (error) {
          console.error('Error loading airline content:', error);
          setAirlineContent(DEFAULT_FALLBACK_CONTENT);
        }
    };

    loadContent();
  }, [slug]);

  useEffect(() => {
    const fetchLocationAndAirport = async () => {
      try {
        const { GeolocationCache } = await import('@/lib/geolocation-cache');
        const data = await GeolocationCache.getGeolocation();
        
        if (data?.latitude && data?.longitude) {
          const currentCoords = {
            latitude: data.latitude,
            longitude: data.longitude,
          };
          setCoords(currentCoords);
          const nearby = getNearbyAirports(currentCoords.latitude, currentCoords.longitude, 1);
          if (nearby.length > 0) {
            setNearestAirport(nearby[0]);
          }
        } else {
          // Failed to get location from IP API
        }
      } catch (error) {
        // Error fetching IP geolocation
      }
    };

    fetchLocationAndAirport();
  }, []);

  useEffect(() => {
    if (!formRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => setSticky(!entry.isIntersecting),
      { rootMargin: '0px', threshold: 0 }
    );

    observer.observe(formRef.current);
    return () => observer.disconnect();
  }, []);

  // Note: Removed loading state to prevent hydration mismatch
  // The component will always render with either loaded content or fallback content

  if (!airlineContent) {
    return null; // or a loading spinner
  }

  const heroSrc = getHeroImage(slug, airlineContent);

  return (
    <main className="min-h-screen w-full bg-white">
      {/* Hero */}
      <section className="relative w-full min-h-[700px] pb-8 md:pb-12">
        <div className="absolute inset-0">
          <Image
            src={heroSrc}
            alt={`${airlineContent.title} aircraft`}
            fill
            priority
            className="object-cover object-center"
          />
          {/* Enhanced overlay for better text readability */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/30 to-black/10" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0FBAB5]/30 via-[#0FBAB5]/75 to-transparent" />
        </div>
        <div className="relative z-10 flex flex-col min-h-[700px]">
          <Navbar isDarkBackground={true} />
          <div className="flex-1 w-full max-w-[1280px] mx-auto px-2 flex flex-col items-center justify-start pt-16">
            <div className="text-white text-center w-full">
              <h1 className="text-6xl sm:text-8xl font-extrabold tracking-tight drop-shadow-lg">{airlineContent.title}</h1>
              <p className="mt-2 text-2xl sm:text-3xl font-semibold drop-shadow-md">{airlineContent.subtitle}</p>
              <p className="mt-4 max-w-3xl mx-auto text-base sm:text-lg text-white/95 drop-shadow-md">
                {airlineContent.description}
              </p>
            </div>
            <div className="mt-8 w-full text-left">
              <AnimatePresence>
                {isSticky && (
                <motion.div
                    className="fixed top-0 left-0 right-0 z-60 shadow-lg"
                    style={{ 
                      backgroundColor: '#0ABAB5',
                      willChange: 'transform',
                      backfaceVisibility: 'hidden',
                      WebkitTransform: 'translateZ(0)',
                      transform: 'translateZ(0)'
                    }}
                    initial={{ y: -120, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: -120, opacity: 0 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                >
                    <div className="max-w-[1280px] mx-auto py-2 px-2">
                        <div className="hidden md:block w-full">
                            <FlightSearchForm 
                              isSticky={isSticky}
                              {...searchState}
                            />
                        </div>
                        <div className="block md:hidden w-full">
                            <StickySearchInput 
                              fromSelection={searchState.fromSelection}
                              toSelection={searchState.toSelection}
                              onClick={() => setIsModalOpen(true)}
                            />
                        </div>
                    </div>
                </motion.div>
                )}
              </AnimatePresence>

              <div ref={formRef} className={`w-full transition-opacity duration-300 ${isSticky ? 'opacity-0' : 'opacity-100'}`}>
                <div className="hidden md:block w-full">
                  <FlightSearchForm 
                    isSticky={isSticky}
                    {...searchState}
                  />
                </div>
                
                <div className="block md:hidden w-full">
                  <FlightSearchFormMobile 
                    {...searchState}
                    coords={coords}
                  />
                </div>
              </div>

              <SearchModal 
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
              >
                <FlightSearchFormMobile 
                  {...searchState}
                  coords={coords}
                />
              </SearchModal>
            </div>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="max-w-[1280px] mx-auto px-2 py-10 sm:py-14 text-center">
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
          {airlineContent.introTitle}
        </h2>
        <p className="mt-4 text-gray-600 leading-relaxed max-w-3xl mx-auto">
          {airlineContent.introText}
        </p>
      </section>

      {/* Features */}
      <AirlineInfoSection 
        airlineSlug={slug} 
        airlineName={airlineContent.title}
        feature={airlineContent.feature}
        seat={airlineContent.seat}
        dining={airlineContent.dining}
        whyChooseTitle={airlineContent.whyChooseTitle}
      />
      

      {/* Airline Deals */}
      <AirlineDealsSection airlineSlug={slug} airlineName={airlineContent.title} />

      {/* Classes */}
      <section className="max-w-[1280px] mx-auto px-2 pt-10 sm:pt-14 pb-10 sm:pb-14">
        <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center mb-8">Travel Classes</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {(airlineContent.classes || airlineContent.travelClasses || DEFAULT_FALLBACK_CONTENT.classes)?.map((travelClass, index) => (
            <div key={index} className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-xl p-6">
              <h4 className="text-xl font-bold text-gray-900 mb-2">{travelClass.name}</h4>
              <p className="text-gray-600 mb-4">{travelClass.description}</p>
              <ul className="space-y-2">
                {travelClass.features.map((feature, featureIndex) => (
                  <li key={featureIndex} className="flex items-center text-sm text-gray-700">
                    <div className="w-2 h-2 bg-[#0ABAB5] rounded-full mr-3"></div>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-[1280px] mx-auto px-2 py-10 sm:py-14 text-center">
        <h3 className="sr-only">Book your flight</h3>
        <p className="text-gray-900 text-lg sm:text-xl font-semibold max-w-4xl mx-auto">
          {airlineContent.ctaTitle}
        </p>
        <p className="mt-3 text-gray-700 text-lg leading-relaxed max-w-4xl mx-auto">
          {airlineContent.ctaText}
        </p>
      </section>
    </main>
  );
}